import React, { useEffect, useState } from "react";
import Container from "../components/Container";
import BreadCrumb from "../components/BreadCrumb";
import Meta from "../components/Meta";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getOrders, updatePreorderPayment } from "../features/user/userSlice";
import { 
  BsBox, 
  BsClock, 
  BsCheckCircle, 
  BsTruck, 
  BsStar,
  BsEye,
  BsCalendar,
  BsCurrencyDollar,
  BsTag,
  BsChevronDown,
  BsChevronUp,
  BsArrowRight,
  BsCalendarEvent,
  BsCreditCard,
  BsDownload
} from "react-icons/bs";
import { AiOutlineShoppingCart } from "react-icons/ai";
import axios from "axios";
import { base_url, config } from "../utils/axiosConfig";

const Orders = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const orderState = useSelector(
    (state) => state?.auth?.getorderedProduct?.orders
  );
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState({});
  const [reviewForms, setReviewForms] = useState({}); // Add this state for review forms
  const [reviewImages, setReviewImages] = useState({}); // Add this state for review images

  const getTokenFromLocalStorage = localStorage.getItem("customer")
    ? JSON.parse(localStorage.getItem("customer"))
    : null;

  const config2 = {
    headers: {
      Authorization: `Bearer ${
        getTokenFromLocalStorage !== null ? getTokenFromLocalStorage.token : ""
      }`,
      Accept: "application/json",
    },
  };

  useEffect(() => {
    dispatch(getOrders(config2));
  }, [dispatch]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'ordered':
        return 'bg-yellow-100 text-yellow-800';
      case 'shipped':
        return 'bg-blue-100 text-blue-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'preorder':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'ordered':
        return <BsClock className="text-yellow-600" />;
      case 'shipped':
        return <BsTruck className="text-blue-600" />;
      case 'delivered':
        return <BsCheckCircle className="text-green-600" />;
      case 'cancelled':
        return <BsBox className="text-red-600" />;
      case 'preorder':
        return <BsCalendarEvent className="text-purple-600" />;
      default:
        return <BsClock className="text-gray-600" />;
    }
  };

  const toggleOrderExpansion = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Check if order contains preorder items
  const hasPreorderItems = (order) => {
    return order?.orderItems?.some(item => item?.product?.isPreorder);
  };

  // Check if preorder date has arrived
  const isPreorderDateArrived = (order) => {
    if (!hasPreorderItems(order)) return false;
    
    const currentDate = new Date();
    return order?.orderItems?.some(item => {
      if (item?.product?.isPreorder && item?.product?.preorderReleaseDate) {
        const releaseDate = new Date(item?.product?.preorderReleaseDate);
        return currentDate >= releaseDate;
      }
      return false;
    });
  };

  // Check if preorder payment has been processed
  const isPreorderPaymentProcessed = (order) => {
    return order?.paymentInfo?.razorpayPaymentId !== "preorder_payment_pending";
  };

  // Load Razorpay script
  const loadScript = (src) => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  // Process preorder payment
  const processPreorderPayment = async (order) => {
    // Set payment processing state
    setPaymentProcessing(prev => ({ ...prev, [order._id]: true }));
    
    try {
      const res = await loadScript(
        "https://checkout.razorpay.com/v1/checkout.js"
      );

      if (!res) {
        alert("Razorpay SDK failed to Load");
        setPaymentProcessing(prev => ({ ...prev, [order._id]: false }));
        return;
      }
      
      // Calculate total amount (including shipping)
      const amountToCharge = order.totalPrice;
      
      const result = await axios.post(
        `${base_url}user/order/checkout`,
        { amount: amountToCharge },
        config
      );

      if (!result) {
        alert("Something Went Wrong");
        setPaymentProcessing(prev => ({ ...prev, [order._id]: false }));
        return;
      }

      const { amount, id: order_id, currency } = result.data.order;

      const options = {
        key: result.data.key_id, // Use key_id from backend response
        amount: amount,
        currency: currency,
        name: "VEDA",
        description: "Preorder Payment",
        order_id: order_id,
        handler: async function (response) {
          const data = {
            orderCreationId: order_id,
            razorpayPaymentId: response.razorpay_payment_id,
            razorpayOrderId: response.razorpay_order_id,
            razorpaySignature: response.razorpay_signature,
          };

          const paymentResult = await axios.post(
            `${base_url}user/order/paymentVerification`,
            data,
            config
          );

          // Update order with payment info using Redux action
          dispatch(updatePreorderPayment({ 
            orderId: order._id, 
            paymentInfo: {
              razorpayPaymentId: response.razorpay_payment_id,
              razorpayOrderId: response.razorpay_order_id,
              razorpaySignature: response.razorpay_signature,
            }
          })).then(() => {
            // Refresh orders
            dispatch(getOrders(config2));
            setPaymentProcessing(prev => ({ ...prev, [order._id]: false }));
          });
        },
        prefill: {
          name: "Dev Corner",
          email: "devcorner@example.com",
          contact: "9999999999",
        },
        notes: {
          address: "developer's corner office",
        },
        theme: {
          color: "#dc3545",
        },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (error) {
      console.error("Payment error:", error);
      alert("Payment failed. Please try again.");
      setPaymentProcessing(prev => ({ ...prev, [order._id]: false }));
    }
  };

  // Handle invoice download
  const handleDownloadInvoice = async (orderId) => {
    try {
      const response = await axios.get(
        `${base_url}user/order/${orderId}/invoice`,
        {
          headers: {
            Authorization: `Bearer ${
              getTokenFromLocalStorage !== null ? getTokenFromLocalStorage.token : ""
            }`,
            Accept: "application/pdf",
          },
          responseType: 'blob' // Important for handling binary data
        }
      );

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${orderId.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading invoice:", error);
      alert("Failed to download invoice. Please try again.");
    }
  };

  // Function to handle review form changes
  const handleReviewChange = (orderId, productId, field, value) => {
    setReviewForms(prev => ({
      ...prev,
      [`${orderId}-${productId}`]: {
        ...prev[`${orderId}-${productId}`],
        [field]: value
      }
    }));
  };

  // Function to handle image selection for reviews
  const handleImageChange = (orderId, productId, files) => {
    // Convert FileList to array and limit to 5 files
    const filesArray = files ? Array.from(files).slice(0, 5) : [];
    
    // Update state by appending new files to existing ones
    setReviewImages(prev => {
      const key = `${orderId}-${productId}`;
      const existingFiles = prev[key] || [];
      // Limit total to 5 files
      const newFiles = [...existingFiles, ...filesArray].slice(0, 5);
      return {
        ...prev,
        [key]: newFiles
      };
    });
  };

  // Function to submit a review
  const submitReview = async (orderId, productId) => {
    const reviewData = reviewForms[`${orderId}-${productId}`];
    const images = reviewImages[`${orderId}-${productId}`];
    
    if (!reviewData || !reviewData.star || !reviewData.comment) {
      alert("Please provide both rating and comment");
      return;
    }
    
    try {
      // Upload images first if any
      let uploadedImages = [];
      if (images && images.length > 0) {
        const formData = new FormData();
        for (let i = 0; i < Math.min(images.length, 5); i++) {
          formData.append('images', images[i]);
        }
        
        const uploadResponse = await axios.post(
          `${base_url}upload/review-images`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${getTokenFromLocalStorage?.token || ""}`
            }
          }
        );
        
        uploadedImages = uploadResponse.data;
      }
      
      // Submit review with images
      await axios.put(
        `${base_url}product/rating`,
        {
          prodId: productId,
          star: reviewData.star,
          comment: reviewData.comment,
          images: uploadedImages
        },
        {
          headers: {
            Authorization: `Bearer ${getTokenFromLocalStorage?.token || ""}`,
            Accept: "application/json"
          }
        }
      );
      
      // Refresh orders
      dispatch(getOrders(config2));
      
      // Also refresh the product data to update total rating
      // Note: This would require a product refresh mechanism
      // For now, we'll rely on the orders refresh which now includes populated ratings
      
      // Clear review form
      setReviewForms(prev => {
        const newForms = { ...prev };
        delete newForms[`${orderId}-${productId}`];
        return newForms;
      });
      
      setReviewImages(prev => {
        const newImages = { ...prev };
        delete newImages[`${orderId}-${productId}`];
        return newImages;
      });
      
      // Clear file input
      const fileInput = document.querySelector(`input[type="file"][onChange*="${orderId}-${productId}"]`);
      if (fileInput) {
        fileInput.value = '';
      }
      
      alert("Review submitted successfully!");
    } catch (error) {
      console.error("Error submitting review:", error);
      alert("Failed to submit review. Please try again.");
    }
  };

  // Check if order can be reviewed (delivered status)
  const canReviewOrder = (order) => {
    return order?.orderStatus?.toLowerCase() === 'delivered';
  };

  if (!orderState || orderState.length === 0) {
    return (
      <>
        <Meta 
          title="My Orders - VEDA Merchandise"
          description="View your order history at VEDA Merchandise."
          keywords="my orders, order history, VEDA orders"
          author="VEDA Merchandise"
          robots="noindex, nofollow"
        />
        <div className="min-h-screen bg-gradient-to-br from-red-50 via-yellow-50 to-red-50 py-12 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-red-100 rounded-full mb-6">
                <AiOutlineShoppingCart className="text-red-500 text-5xl" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">No Orders Yet</h2>
              <p className="text-gray-600 mb-8 text-lg">
                You haven't placed any orders yet. Start shopping to see your orders here.
              </p>
              <button
                onClick={() => window.location.href = '/products'}
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-red-500 to-red-600 text-white font-bold rounded-full shadow-lg hover:from-red-600 hover:to-red-700 transition-all transform hover:scale-105"
              >
                <AiOutlineShoppingCart className="text-xl" />
                <span>Start Shopping</span>
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Schema markup for structured data
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "My Orders - VEDA Merchandise",
    "description": "View your order history at VEDA Merchandise.",
    "url": "https://vedamerchandise.com/my-orders",
    "publisher": {
      "@type": "Organization",
      "name": "VEDA Merchandise",
      "logo": {
        "@type": "ImageObject",
        "url": "https://vedamerchandise.com/logo.svg"
      }
    }
  };

  return (
    <>
      <Meta 
        title="My Orders - VEDA Merchandise"
        description="View your order history at VEDA Merchandise."
        keywords="my orders, order history, VEDA orders"
        author="VEDA Merchandise"
        ogTitle="My Orders - VEDA Merchandise"
        ogDescription="View your order history at VEDA Merchandise."
        ogImage="https://vedamerchandise.com/orders-preview.jpg"
        ogUrl="https://vedamerchandise.com/my-orders"
        ogType="website"
        twitterCard="summary_large_image"
        twitterTitle="My Orders - VEDA Merchandise"
        twitterDescription="View your order history at VEDA Merchandise."
        twitterImage="https://vedamerchandise.com/orders-preview.jpg"
        robots="noindex, nofollow"
        canonicalUrl="https://vedamerchandise.com/my-orders"
        schemaMarkup={schemaMarkup}
      />
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-yellow-50 to-red-50 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">My Orders</h1>
                <p className="text-gray-600">Track your orders and view order history</p>
              </div>
              <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6">
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-red-500 mb-1">
                    {orderState?.length || 0}
                  </div>
                  <div className="text-sm text-gray-600">Total Orders</div>
                </div>
              </div>
            </div>
          </div>

          {/* Orders List */}
          <div className="space-y-4">
            {orderState?.map((order, orderIndex) => (
              <div key={orderIndex} className="bg-white rounded-3xl shadow-lg border-2 border-gray-100 overflow-hidden">
                {/* Order Header - Clickable */}
                <div
                  onClick={() => toggleOrderExpansion(order._id)}
                  className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    {/* Order ID & Date */}
                    <div className="flex-grow grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Order ID</div>
                        <div className="font-semibold text-gray-900 truncate text-sm">
                          {order?.customOrderId || `#${order._id.slice(-8).toUpperCase()}`}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Date</div>
                        <div className="font-semibold text-gray-900">
                          {formatDate(order.createdAt)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Total</div>
                        <div className="font-bold text-red-500 text-lg">
                          ₹{order.totalPrice?.toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Status</div>
                        <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold ${getStatusColor(order.orderStatus)}`}>
                          {getStatusIcon(order.orderStatus)}
                          <span>{order.orderStatus}</span>
                        </span>
                      </div>
                    </div>

                    {/* Expand Icon */}
                    <div className="flex justify-center lg:justify-end">
                      <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full">
                        {expandedOrder === order._id ? (
                          <BsChevronUp className="text-xl text-gray-600" />
                        ) : (
                          <BsChevronDown className="text-xl text-gray-600" />
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Preorder Notice */}
                  {hasPreorderItems(order) && (
                    <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-2xl">
                      <div className="flex items-center gap-2">
                        <BsCalendarEvent className="text-purple-600" />
                        <span className="text-sm text-purple-700 font-medium">
                          This order contains preorder items
                        </span>
                      </div>
                      {/* Pay Now Button for eligible preorder orders */}
                      {hasPreorderItems(order) && isPreorderDateArrived(order) && !isPreorderPaymentProcessed(order) && (
                        <div className="mt-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              processPreorderPayment(order);
                            }}
                            disabled={paymentProcessing[order._id]}
                            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white font-semibold rounded-full hover:from-purple-600 hover:to-purple-700 transition-all disabled:opacity-50"
                          >
                            <BsCreditCard />
                            <span>{paymentProcessing[order._id] ? "Processing..." : "Pay Now"}</span>
                          </button>
                          <p className="text-xs text-purple-600 mt-1">
                            Preorder items are now available. Complete payment to process your order.
                          </p>
                        </div>
                      )}
                      {isPreorderPaymentProcessed(order) && (
                        <div className="mt-3">
                          <span className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-800 text-sm font-semibold rounded-full">
                            <BsCheckCircle className="text-green-600" />
                            <span>Payment Completed</span>
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Order Details - Expandable */}
                {expandedOrder === order._id && (
                  <div className="border-t-2 border-gray-100 p-6 bg-gradient-to-br from-gray-50 to-white">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                            <BsCurrencyDollar className="text-red-500 text-xl" />
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">Total Amount</div>
                            <div className="font-bold text-gray-900">₹{order.totalPrice?.toLocaleString()}</div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                            <BsTag className="text-yellow-600 text-xl" />
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">After Discount</div>
                            <div className="font-bold text-gray-900">₹{order.totalPriceAfterDiscount?.toLocaleString()}</div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <BsCalendar className="text-blue-600 text-xl" />
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">Order Date</div>
                            <div className="font-bold text-gray-900">{formatDate(order.createdAt)}</div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <BsBox className="text-green-600 text-xl" />
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">Items</div>
                            <div className="font-bold text-gray-900">{order.orderItems?.length || 0}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Products List */}
                    <div className="mb-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Order Items</h3>
                      <div className="space-y-3">
                        {order.orderItems?.map((item, itemIndex) => (
                          <div key={itemIndex} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                            <div className="flex flex-col sm:flex-row gap-4">
                              {/* Product Image */}
                              <div className="flex-shrink-0 relative">
                                <img
                                  src={item.product?.images?.[0]?.url || "https://via.placeholder.com/100"}
                                  alt={item.product?.title}
                                  className="w-full sm:w-24 h-24 object-cover rounded-xl"
                                />
                                {/* Preorder Badge */}
                                {item?.product?.isPreorder && (
                                  <span className="absolute top-2 right-2 bg-purple-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                                    Preorder
                                  </span>
                                )}
                              </div>

                              {/* Product Details */}
                              <div className="flex-grow">
                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                                  <div className="flex-grow">
                                    <h4 className="font-bold text-gray-900 mb-1">
                                      {item.product?.title}
                                    </h4>
                                    <p className="text-sm text-gray-600 mb-2">
                                      Brand: {item.product?.brand}
                                    </p>
                                    {/* Preorder Info */}
                                    {item?.product?.isPreorder && (
                                      <div className="mt-2">
                                        <span className="inline-block bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">
                                          Preorder Item
                                        </span>
                                        {item?.product?.preorderReleaseDate && (
                                          <p className="text-xs text-gray-500 mt-1">
                                            Estimated Release: {formatDate(item?.product?.preorderReleaseDate)}
                                          </p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex sm:flex-col items-center sm:items-end gap-4 sm:gap-2">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm text-gray-600">Qty:</span>
                                      <span className="px-3 py-1 bg-gray-100 rounded-full text-sm font-bold">
                                        {item.quantity}
                                      </span>
                                    </div>
                                    <div className="text-right">
                                      <div className="text-sm text-gray-500">₹{item.price?.toLocaleString()} each</div>
                                      <div className="font-bold text-lg text-red-500">
                                        ₹{(item.price * item.quantity)?.toLocaleString()}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Order Actions */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pt-4 border-t-2 border-gray-100">
                      <div className="text-sm text-gray-600">
                        Order placed on {formatDate(order.createdAt)}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button className="flex items-center gap-2 px-4 py-2 border-2 border-gray-300 text-gray-700 font-semibold rounded-full hover:bg-gray-50 transition-colors">
                          <BsEye />
                          <span className="hidden sm:inline">View Details</span>
                        </button>
                        {canReviewOrder(order) && (
                          <button 
                            onClick={() => {
                              // Scroll to the review section for this order
                              const reviewSection = document.getElementById(`review-${order._id}`);
                              if (reviewSection) {
                                reviewSection.scrollIntoView({ behavior: 'smooth' });
                                // Expand the order if it's not already expanded
                                if (expandedOrder !== order._id) {
                                  setExpandedOrder(order._id);
                                }
                              }
                            }}
                            className="flex items-center gap-2 px-4 py-2 border-2 border-yellow-400 text-yellow-700 font-semibold rounded-full hover:bg-yellow-50 transition-colors"
                          >
                            <BsStar />
                            <span className="hidden sm:inline">Review Products</span>
                          </button>
                        )}
                        <button 
                          onClick={() => handleDownloadInvoice(order._id)}
                          className="flex items-center gap-2 px-4 py-2 border-2 border-green-500 text-green-700 font-semibold rounded-full hover:bg-green-50 transition-colors"
                        >
                          <BsDownload />
                          <span className="hidden sm:inline">Download Invoice</span>
                        </button>
                        <button 
                          onClick={() => navigate(`/track-order/${order._id}`)}
                          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-full hover:from-red-600 hover:to-red-700 transition-all"
                        >
                          <BsTruck />
                          <span>Track Order</span>
                        </button>
                      </div>
                    </div>

                    {/* Review Section for Delivered Orders */}
                    {canReviewOrder(order) && expandedOrder === order._id && (
                      <div className="mt-6 p-4 bg-yellow-50 rounded-2xl border border-yellow-200">
                        <h4 className="text-lg font-bold text-gray-900 mb-4">Review Your Products</h4>
                        <div className="space-y-6">
                          {order.orderItems?.map((item, itemIndex) => (
                            <div key={itemIndex} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                              <div className="flex flex-col sm:flex-row gap-4">
                                {/* Product Image */}
                                <div className="flex-shrink-0">
                                  <img
                                    src={item.product?.images?.[0]?.url || "https://via.placeholder.com/100"}
                                    alt={item.product?.title}
                                    className="w-24 h-24 object-cover rounded-xl"
                                  />
                                </div>
                                
                                {/* Product Details and Review Form */}
                                <div className="flex-grow">
                                  <h5 className="font-bold text-gray-900 mb-2">{item.product?.title}</h5>
                                  
                                  {/* Existing Ratings */}
                                  {item.product?.ratings && item.product.ratings.length > 0 && (
                                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                                      <div className="text-sm text-gray-600 mb-2">
                                        {item.product.ratings.length} review{item.product.ratings.length > 1 ? 's' : ''}
                                      </div>
                                      <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                                        {item.product.ratings.map((rating, ratingIndex) => (
                                          <div key={ratingIndex} className="flex items-start gap-2 text-sm">
                                            <div className="flex-shrink-0 w-6 h-6 bg-red-100 rounded-full flex items-center justify-center text-red-600 font-bold text-xs">
                                              {rating?.postedby?.firstname?.charAt(0)?.toUpperCase() || 'U'}
                                            </div>
                                            <div>
                                              <div className="font-medium">
                                                {rating?.postedby?.firstname ? `${rating.postedby.firstname} ${rating.postedby.lastname || ''}` : 'User'}
                                              </div>
                                              <div className="flex items-center gap-1">
                                                {[...Array(5)].map((_, i) => (
                                                  <span key={i} className={i < rating.star ? 'text-yellow-400' : 'text-gray-300'}>
                                                    ★
                                                  </span>
                                                ))}
                                              </div>
                                              <div className="text-gray-600 mt-1">{rating.comment}</div>
                                              {rating?.createdAt && (
                                                <div className="text-gray-500 text-xs mt-1">
                                                  {new Date(rating.createdAt).toLocaleDateString()}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Review Form */}
                                  <div className="mt-3">
                                    <div className="mb-2">
                                      <label className="block text-sm font-medium text-gray-700 mb-1">Rating</label>
                                      <div className="flex items-center">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                          <button
                                            key={star}
                                            type="button"
                                            onClick={() => handleReviewChange(order._id, item.product?._id, 'star', star)}
                                            className="text-2xl focus:outline-none"
                                          >
                                            <span className={reviewForms[`${order._id}-${item.product?._id}`]?.star >= star ? 'text-yellow-400' : 'text-gray-300'}>
                                              ★
                                            </span>
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                    
                                    <div className="mb-2">
                                      <label className="block text-sm font-medium text-gray-700 mb-1">Comment</label>
                                      <textarea
                                        rows="3"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                                        placeholder="Share your experience with this product..."
                                        value={reviewForms[`${order._id}-${item.product?._id}`]?.comment || ''}
                                        onChange={(e) => handleReviewChange(order._id, item.product?._id, 'comment', e.target.value)}
                                      />
                                    </div>
                                    
                                    <div className="mb-3">
                                      <label className="block text-sm font-medium text-gray-700 mb-1">Photos (Optional)</label>
                                      <input
                                        type="file"
                                        multiple
                                        accept="image/*"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                                        onChange={(e) => handleImageChange(order._id, item.product?._id, e.target.files)}
                                      />
                                      <p className="text-xs text-gray-500 mt-1">
                                        You can upload up to 5 photos 
                                        ({reviewImages[`${order._id}-${item.product?._id}`] ? reviewImages[`${order._id}-${item.product?._id}`].length : 0} selected)
                                      </p>
                                      
                                      {/* Preview selected images */}
                                      {reviewImages[`${order._id}-${item.product?._id}`] && reviewImages[`${order._id}-${item.product?._id}`].length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-2">
                                          {reviewImages[`${order._id}-${item.product?._id}`].map((file, index) => (
                                            <div key={index} className="relative" style={{ width: '60px', height: '60px' }}>
                                              <img
                                                src={URL.createObjectURL(file)}
                                                alt={`Preview ${index + 1}`}
                                                className="img-fluid rounded"
                                                style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                              />
                                              <button 
                                                type="button"
                                                className="btn btn-danger btn-sm position-absolute top-0 end-0 p-1" 
                                                style={{ minWidth: '20px', height: '20px', fontSize: '10px' }}
                                                onClick={() => {
                                                  setReviewImages(prev => {
                                                    const key = `${order._id}-${item.product?._id}`;
                                                    const existingFiles = prev[key] || [];
                                                    const newFiles = existingFiles.filter((_, i) => i !== index);
                                                    return {
                                                      ...prev,
                                                      [key]: newFiles
                                                    };
                                                  });
                                                }}
                                              >
                                                ×
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                    
                                    <button
                                      onClick={() => submitReview(order._id, item.product?._id)}
                                      className="px-4 py-2 bg-yellow-500 text-white font-semibold rounded-lg hover:bg-yellow-600 transition-colors"
                                    >
                                      Submit Review
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Orders;